<template>
    <div class="conflict-row" :class="{ 'is-resolved': isResolved }">
        <div class="conflict-row-head">
            <div class="conflict-row-info">
                <span class="conflict-entity-name">{{ entityLabel }}</span>
                <span class="conflict-entity-meta">
                    {{ conflict.entity_type }} &middot;
                    {{ conflict.conflict_kind }}
                </span>
            </div>
            <Tag
                v-if="isResolved"
                :value="`resolved · ${conflict.resolution}`"
                severity="success"
            />
            <Tag v-else value="pending" severity="warn" />
        </div>

        <div v-if="!isResolved" class="conflict-row-actions">
            <Button
                label="Keep main"
                size="small"
                outlined
                :loading="busy === 'keep_main'"
                :disabled="anyBusy"
                data-testid="keep-main"
                @click="pick('keep_main')"
            />
            <Button
                label="Keep thread"
                size="small"
                outlined
                :loading="busy === 'keep_thread'"
                :disabled="anyBusy"
                data-testid="keep-thread"
                @click="pick('keep_thread')"
            />
            <Button
                label="Open & edit"
                size="small"
                text
                icon="pi pi-pencil"
                :disabled="anyBusy"
                data-testid="open-edit"
                @click="toggleEdit"
            />
        </div>

        <!-- 3-way diff + edit surface -->
        <div v-if="expanded" class="conflict-diff" data-testid="conflict-diff">
            <div v-if="loadingVersions" class="conflict-diff-loading">
                <ProgressSpinner style="width: 2rem; height: 2rem" />
            </div>
            <template v-else-if="versions">
                <div class="conflict-diff-pane">
                    <h4>What main changed</h4>
                    <FieldDiff
                        v-if="isMetaOnly"
                        :from="baseObj"
                        :to="masterObj"
                        fromName="Base"
                        toName="Main"
                    />
                    <DiffViewer
                        v-else
                        :from="baseBlob"
                        :to="masterBlob"
                        :mimetype="versions.content_type"
                        diffFormat="line-by-line"
                    />
                </div>
                <div class="conflict-diff-pane">
                    <h4>What this thread changed</h4>
                    <FieldDiff
                        v-if="isMetaOnly"
                        :from="baseObj"
                        :to="branchObj"
                        fromName="Base"
                        toName="Thread"
                    />
                    <DiffViewer
                        v-else
                        :from="baseBlob"
                        :to="branchBlob"
                        :mimetype="versions.content_type"
                        diffFormat="line-by-line"
                    />
                </div>
                <div class="conflict-edit">
                    <label :for="`conflict-edit-${rowKey}`">
                        Edited resolution
                    </label>
                    <Textarea
                        :id="`conflict-edit-${rowKey}`"
                        v-model="editedContent"
                        rows="6"
                        class="w-full conflict-edit-area"
                    />
                    <div class="conflict-edit-actions">
                        <Button
                            label="Save edited"
                            size="small"
                            icon="pi pi-check"
                            :loading="busy === 'edited'"
                            :disabled="anyBusy || !editedContent.trim()"
                            data-testid="save-edited"
                            @click="saveEdited"
                        />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import { computed, ref } from "vue";

import DiffViewer from "@/components/Diff/DiffViewer.vue";
import FieldDiff from "@/components/Diff/FieldDiff.vue";
import {
    type ConflictRecordSchema,
    type ConflictResolution,
    type EntityVersionsSchema,
    useThreadsStore,
} from "@/stores/threads-store";

const props = defineProps<{
    threadId: string;
    conflict: ConflictRecordSchema;
}>();

const store = useThreadsStore();

const isResolved = computed(() => props.conflict.status === "resolved");
const entityLabel = computed(
    () => props.conflict.name || props.conflict.entity_id
);
const rowKey = computed(
    () => `${props.conflict.entity_type}-${props.conflict.entity_id}`
);

const busy = ref<ConflictResolution | null>(null);
const anyBusy = computed(() => busy.value !== null);

const expanded = ref(false);
const loadingVersions = ref(false);
const versions = ref<EntityVersionsSchema | undefined>(undefined);
const editedContent = ref("");

const asBlob = (text: string | null | undefined, type: string): Blob =>
    new Blob([text ?? ""], { type });

const baseBlob = computed(() =>
    asBlob(versions.value?.base, versions.value?.content_type ?? "text/plain")
);
const masterBlob = computed(() =>
    asBlob(versions.value?.master, versions.value?.content_type ?? "text/plain")
);
const branchBlob = computed(() =>
    asBlob(versions.value?.branch, versions.value?.content_type ?? "text/plain")
);

// A metadata-only conflict (a renamed domain, a tweaked attribute) is the
// entity's `_meta.json` — `application/json`, not BPMN and not a rich-text doc.
// Those render as a structured field table rather than a JSON-text char diff;
// BPMN/description/artefact keep flowing through DiffViewer unchanged.
const isMetaOnly = computed(
    () => versions.value?.content_type === "application/json"
);

const parseSide = (
    text: string | null | undefined
): Record<string, unknown> | null => {
    if (text == null) return null;
    try {
        const parsed = JSON.parse(text);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
};

const baseObj = computed(() => parseSide(versions.value?.base));
const masterObj = computed(() => parseSide(versions.value?.master));
const branchObj = computed(() => parseSide(versions.value?.branch));

const pick = async (resolution: ConflictResolution) => {
    if (anyBusy.value) return;
    busy.value = resolution;
    await store.resolveConflict(
        props.threadId,
        props.conflict.entity_type,
        props.conflict.entity_id,
        resolution
    );
    busy.value = null;
};

const toggleEdit = async () => {
    expanded.value = !expanded.value;
    if (expanded.value && !versions.value) {
        loadingVersions.value = true;
        versions.value = await store.readEntityVersions(
            props.threadId,
            props.conflict.entity_type,
            props.conflict.entity_id
        );
        // Seed the editor with the thread's (branch) version as the starting point.
        editedContent.value = versions.value?.branch ?? "";
        loadingVersions.value = false;
    }
};

const saveEdited = async () => {
    if (anyBusy.value || !editedContent.value.trim()) return;
    busy.value = "edited";
    await store.resolveConflict(
        props.threadId,
        props.conflict.entity_type,
        props.conflict.entity_id,
        "edited",
        editedContent.value
    );
    busy.value = null;
};
</script>

<style scoped>
.conflict-row {
    border: 1px solid var(--p-surface-200);
    padding: 0.6rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.conflict-row.is-resolved {
    opacity: 0.7;
}

.conflict-row-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.conflict-row-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.conflict-entity-name {
    font-weight: 600;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.conflict-entity-meta {
    font-size: 0.72rem;
    color: var(--p-text-muted-color);
    font-family: monospace;
}

.conflict-row-actions {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.conflict-diff {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    border-top: 1px solid var(--p-surface-200);
    padding-top: 0.5rem;
}

.conflict-diff-loading {
    display: flex;
    justify-content: center;
    padding: 1rem 0;
}

.conflict-diff-pane h4 {
    margin: 0 0 0.25rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--p-text-secondary-color);
}

.conflict-edit {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.conflict-edit label {
    font-size: 0.78rem;
    font-weight: 600;
}

.conflict-edit-area {
    font-family: monospace;
    font-size: 0.8rem;
}

.conflict-edit-actions {
    display: flex;
    justify-content: flex-end;
}
</style>
